import type { Category, Idea } from "../../shared/types";

/** Matches Astryx DateInput's ISODateString template-literal type. */
export type IsoDate = `${number}${number}${number}${number}-${number}${number}-${number}${number}`;

type TokenColor =
  | "default"
  | "red"
  | "orange"
  | "yellow"
  | "green"
  | "teal"
  | "cyan"
  | "blue"
  | "purple"
  | "pink"
  | "gray";

export const CATEGORY_META: Record<Category, { label: string; color: TokenColor }> = {
  do: { label: "Do", color: "blue" },
  buy: { label: "Buy", color: "green" },
  go: { label: "Go", color: "orange" },
  try: { label: "Try", color: "purple" },
};

/** "buy a monitor" stays text; "https://…" and "www.…" become links. */
export function normalizeUrl(raw: string): string | null {
  const t = raw.trim();
  if (/^https?:\/\/\S+$/i.test(t)) return t;
  if (/^www\.\S+\.\S+$/i.test(t)) return `https://${t}`;
  return null;
}

const URL_IN_TEXT = /(https?:\/\/[^\s<>"'()]+|www\.[^\s<>"'()]+\.[^\s<>"'()]+)/gi;

/** Every URL found anywhere in free text, normalized and deduped. */
export function extractUrls(text: string): string[] {
  const seen = new Set<string>();
  for (const match of text.match(URL_IN_TEXT) ?? []) {
    const cleaned = match.replace(/[.,;:!?]+$/, "");
    const url = normalizeUrl(cleaned);
    if (url) seen.add(url);
  }
  return [...seen];
}

export interface ParsedInput {
  title: string;
  notes: string;
  urls: string[];
}

/**
 * Turn raw composer text into an idea: URLs (anywhere in the text) become
 * links, the first non-empty line of what remains becomes the title, the rest
 * becomes notes. A bare URL gets its domain as a placeholder title.
 */
export function parseComposerText(raw: string): ParsedInput | null {
  const urls = extractUrls(raw);
  let text = raw;
  for (const url of urls) {
    text = text.replaceAll(url, " ").replaceAll(url.replace(/^https:\/\//, ""), " ");
  }
  const lines = text
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter((l) => l.length > 0);
  const title = lines[0] ?? (urls[0] ? domainOf(urls[0]) : "");
  if (!title) return null;
  return { title: title.slice(0, 300), notes: lines.slice(1).join("\n"), urls };
}

export function domainOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function faviconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
}

export interface DueInfo {
  label: string;
  color: TokenColor;
  overdue: boolean;
}

export function dueInfo(due: string | null): DueInfo | null {
  if (!due) return null;
  const [y, m, d] = due.split("-").map(Number);
  if (!y || !m || !d) return null;
  const target = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);

  if (days < 0) return { label: `${-days}d overdue`, color: "red", overdue: true };
  if (days === 0) return { label: "today", color: "orange", overdue: false };
  if (days === 1) return { label: "tomorrow", color: "yellow", overdue: false };
  if (days <= 7) return { label: `in ${days}d`, color: "yellow", overdue: false };
  const fmt: Intl.DateTimeFormatOptions =
    target.getFullYear() === today.getFullYear()
      ? { month: "short", day: "numeric" }
      : { month: "short", day: "numeric", year: "numeric" };
  return { label: target.toLocaleDateString("en-US", fmt), color: "gray", overdue: false };
}

export function matchesQuery(idea: Idea, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (idea.title.toLowerCase().includes(needle)) return true;
  if (idea.notes.toLowerCase().includes(needle)) return true;
  return idea.links.some(
    (l) =>
      l.domain.toLowerCase().includes(needle) ||
      (l.title ?? "").toLowerCase().includes(needle),
  );
}

/** Pinned first (in their manual order), then newest. */
export function sortIdeas(list: Idea[]): Idea[] {
  return [...list].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    if (a.pinned && b.pinned && a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return b.createdAt - a.createdAt;
  });
}
